import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kHTMLCore } from "../../../Admin/kReusableCoreTS/kHTMLCore";
import { AppSearch, AppSearchChat, AppSearchData, AppSearchFilter, AppSearchHit, IAppSearchHitsMarkup } from "./AppSearch";
import { AppIcon, AppIcons } from "./AppIcons";
import { AppReactRules } from "./AppReactRules";
import { AppAvatarRules } from "./AppAvatars";
import { AppChatQuickView } from "./AppChatQuickView";
import { AppContact } from "./AppIndex";
import { AppAssistApi } from "./AppAssistApi";

// AppSearchActions - ACT ON A MESSAGE WHERE THE SEARCH FOUND IT (owner, 2026-10-22): hits grouped per contact (disc,
// name, count, Open chat, Chat settings), and on each hit a row of icon buttons - Reply, React (the six faces), Help me
// reply (AI), Forward, Remind me, Copy text.
//
// ⚠ RULES AND MARKUP ONLY - no `window`, no `document` (the Bridge suite imports this file). The click side is
// `AppSearchActRun`; the page side is `AppMain` (`IAppSearchActHost`).
//
// ⛔ DECISION 9: NOTHING HERE SENDS. Reply only opens the chat with the box in reply mode (Send is still the owner's
// press); Forward only opens the picker (the contact's click sends); Help me reply only ASKS the AI; a reaction goes
// only from a click on one of the six faces, through the page's ONE sender (`AppMain.QueueReaction`).
// ⚠ Reply / React / Forward / Help me reply need WhatsApp's message id (`search.json` tuple element 5); a hit from an
// older file without one offers only Remind me and Copy text.

/** What the page can do for the hits it draws. */
export class AppSearchActOpts {
    /** `/send` is reachable with this browser's key (reply, react, forward). */
    public CanSend: boolean = false;
    /** The owner's AI panel is available. */
    public CanAi: boolean = false;
    /** `/tasks` takes an own task (tasks on, service, key). */
    public CanTask: boolean = false;
    /** The service's search: a hit opens through `data-search-chat` / `data-search-message` (a context dialog). */
    public Remote: boolean = false;
    /** Index href -> the contact's `Avatar.jpg` href ("" = initials). */
    public Faces: Record<string, string> = {};
}

/** One contact's hits on this page, newest first. */
export class AppSearchGroup {
    public Chat: AppSearchChat = new AppSearchChat();
    public Hits: AppSearchHit[] = [];
    /** Each hit's place in the page's flat list (`data-sa-hit`). */
    public At: number[] = [];
}

export class AppSearchActRules {
    public static readonly Reply: string = "reply";
    public static readonly React: string = "react";
    public static readonly Ai: string = "ai";
    public static readonly Forward: string = "forward";
    public static readonly Remind: string = "remind";
    public static readonly Copy: string = "copy";
    /** A face pressed - the one action that sends (a reaction), and only on this click. */
    public static readonly Emoji: string = "emoji";
    /** ⭐ (owner 2026-11-01) The "⋯" that folds a hit's actions open on a phone (below 1100px; hidden on the wide screen). */
    public static readonly More: string = "more";
    public static readonly OpenAttr: string = "data-sa-open";
    public static readonly ActAttr: string = "data-sact";
    public static readonly HitAttr: string = "data-sa-hit";
    public static readonly EmojiAttr: string = "data-sa-emoji";
    /** A task label is at most this many code points (the service takes 200). */
    public static readonly LabelChars: number = 120;

    /** A 1:1 chat id (search.json holds 1:1 chats only; a group id never gets a send action). */
    public static OneToOne = (ChatId: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Id: string = nz(ChatId);
            return Id.length > 0 && /@g\.us$|@broadcast$|@newsletter$/.test(Id) === false;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRules.OneToOne");
            return false;
        }
    };

    /** The actions one hit offers, in the order they are drawn. */
    public static ActsFor = (Hit: AppSearchHit, Opts: AppSearchActOpts): string[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: string[] = [];
            const HasId: boolean = nz(Hit.Msg.MessageId).length > 0;
            const Direct: boolean = AppSearchActRules.OneToOne(Hit.Chat.ChatId);
            const Sendable: boolean = HasId === true && Direct === true && Opts.CanSend === true;
            if (Sendable === true) { Out.push(AppSearchActRules.Reply); Out.push(AppSearchActRules.React); }
            if (HasId === true && Direct === true && Opts.CanAi === true && Hit.Msg.FromMe !== true) { Out.push(AppSearchActRules.Ai); }
            if (Sendable === true) { Out.push(AppSearchActRules.Forward); }
            if (Opts.CanTask === true && Direct === true && nz(Hit.Chat.Href).length > 0) { Out.push(AppSearchActRules.Remind); }
            if (nz(AppSearch.MatchedText(Hit)).length > 0) { Out.push(AppSearchActRules.Copy); }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRules.ActsFor");
            return [];
        }
    };

    /** Hits by contact: groups in the order of their newest hit, hits newest first inside (the page's order). */
    public static Group = (Hits: AppSearchHit[]): AppSearchGroup[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: AppSearchGroup[] = [];
            const ByKey: Record<string, AppSearchGroup> = {};
            for (let I = 0; I < Hits.length; I++) {
                const Hit: AppSearchHit = Hits[I];
                const Key: string = "k:" + nz(Hit.Chat.Href) + "|" + nz(Hit.Chat.ChatId);
                let Group: AppSearchGroup | undefined = ByKey[Key];
                if (Group === undefined) {
                    Group = new AppSearchGroup();
                    Group.Chat = Hit.Chat;
                    ByKey[Key] = Group;
                    Out.push(Group);
                }
                Group.Hits.push(Hit);
                Group.At.push(I);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRules.Group");
            return [];
        }
    };

    /** The own task's label: the message's words on one line, cut by code point with "…". */
    public static RemindLabel = (Text: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const One: string = nz(Text).replace(/\s+/g, " ").trim();
            const Points: string[] = Array.from(One);
            if (Points.length <= AppSearchActRules.LabelChars) { return One; }
            return Points.slice(0, AppSearchActRules.LabelChars - 1).join("").trim() + "…";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRules.RemindLabel");
            return "";
        }
    };

    /** The own task's second line: whose message and when ("" parts left out). */
    public static RemindValue = (Hit: AppSearchHit): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Day: string = AppSearch.DateOf(Hit.Msg.Minute);
            let Who: string = "From " + nz(Hit.Chat.Title);
            if (Hit.Msg.FromMe === true) { Who = "You wrote to " + nz(Hit.Chat.Title); }
            if (Day.length > 0) { return Who + " on " + Day; }
            return Who;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRules.RemindValue");
            return "";
        }
    };

    /** The AI panel's question. ⛔ Asking only - the draft card's Send is still the only sender. */
    public static AiAsk = (Hit: AppSearchHit): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Words: string = nz(AppSearch.MatchedText(Hit)).replace(/\s+/g, " ").trim();
            return ("Help me reply to this message from " + nz(Hit.Chat.Title) + ": \"" + Words + "\"").substring(0, AppAssistApi.MaxAsk);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActRules.AiAsk");
            return "";
        }
    };

    /** The words of each action's button (aria-label and title). DOCUMENTED EXCEPTION: a lookup of constants.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static LabelOf = (Act: string): string => {
        if (Act === AppSearchActRules.Reply) { return "Reply"; }
        if (Act === AppSearchActRules.React) { return "React"; }
        if (Act === AppSearchActRules.Ai) { return "Help me reply"; }
        if (Act === AppSearchActRules.Forward) { return "Forward"; }
        if (Act === AppSearchActRules.Remind) { return "Remind me"; }
        if (Act === AppSearchActRules.Copy) { return "Copy text"; }
        return "";
    };

    /** DOCUMENTED EXCEPTION: a lookup of constants. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static IconOf = (Act: string): AppIcon => {
        if (Act === AppSearchActRules.Reply) { return AppIcons.Reply; }
        if (Act === AppSearchActRules.React) { return AppIcons.React; }
        if (Act === AppSearchActRules.Ai) { return AppIcons.Spark; }
        if (Act === AppSearchActRules.Forward) { return AppIcons.Forward; }
        if (Act === AppSearchActRules.Remind) { return AppIcons.Plus; }
        return AppIcons.Copy;
    };

    // ── the states a hit's line says (role=status, ink) ──
    public static readonly ReactingWords: string = "Reacting…";
    public static readonly ReactQueuedWords: string = "Reaction on its way…";
    public static readonly ReactSentWords: string = "Reaction sent.";
    public static readonly ReactUnconfirmedWords: string = "Reaction not confirmed - check the chat on your phone.";
    public static readonly ReactWaitingWords: string = "Reaction still queued.";
    public static readonly RemindBusyWords: string = "Adding a task…";
    public static readonly RemindDoneWords: string = "Added to your tasks.";
    public static readonly CopiedWords: string = "Copied.";
    // REUSE-JUSTIFIED: a refused clipboard is the browser's answer the owner can act on (fate 1, shown in ink, as AppAssistTools does); the handler would latch every control for the visit (CLAUDE.md "EXPECTED CONDITIONS MUST NEVER REACH THE HANDLER").
    public static readonly CopyRefusedWhy: string = "Couldn't copy - select the text and copy it by hand.";
}

/** The search actions' hits for `AppSearch.ResultsWith`: grouped per contact, filling `OutHits` in `data-sa-hit` order. */
export class AppSearchActHits implements IAppSearchHitsMarkup {
    private readonly Opts: AppSearchActOpts;
    private readonly OutHits: AppSearchHit[];

    public constructor(Opts: AppSearchActOpts, OutHits: AppSearchHit[]) {
        this.Opts = Opts;
        this.OutHits = OutHits;
    }

    public Markup(Hits: AppSearchHit[], Words: string[]): string {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppSearchActView.Groups(Hits, Words, this.Opts, this.OutHits);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActHits.Markup");
            return "";
        }
    }
}

export class AppSearchActView {
    /**
     * The "Messages" section, grouped per contact, with each hit's actions. `OutHits` receives the page's hits in the
     * order `data-sa-hit` numbers them (the click side reads them back by that number).
     */
    public static ResultsHtml = (Data: AppSearchData, Query: string, Filter: AppSearchFilter, Opts: AppSearchActOpts,
                                 OutHits: AppSearchHit[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            return AppSearch.ResultsWith(Data, Query, Filter, new AppSearchActHits(Opts, OutHits));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActView.ResultsHtml");
            return "";
        }
    };

    /** Every group, in a list of groups (`ul.sgroups`). */
    public static Groups = (Hits: AppSearchHit[], Words: string[], Opts: AppSearchActOpts, OutHits: AppSearchHit[]): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            OutHits.length = 0;
            for (const Hit of Hits) { OutHits.push(Hit); }
            const Out: string[] = ["<ul class=\"sgroups\">"];
            for (const Group of AppSearchActRules.Group(Hits)) { Out.push(AppSearchActView.GroupHtml(Group, Words, Opts)); }
            Out.push("</ul>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActView.Groups");
            return "";
        }
    };

    /** One contact: disc, name, count, Open chat, Chat settings - and its hits. */
    public static GroupHtml = (Group: AppSearchGroup, Words: string[], Opts: AppSearchActOpts): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Chat: AppSearchChat = Group.Chat;
            let Face: string = "";
            if (Object.prototype.hasOwnProperty.call(Opts.Faces, Chat.Href) === true) { Face = Opts.Faces[Chat.Href]; }
            const Count: number = Group.Hits.length;
            let CountWords: string = String(Count) + " messages";
            if (Count === 1) { CountWords = "1 message"; }
            const Out: string[] = ["<li class=\"sgroup\"><div class=\"sghead\">", AppAvatarRules.Disc(Chat.Title, Face),
                "<span class=\"sgname\">" + kHTMLCore.EscapeHtml(Chat.Title) + "</span><span class=\"sgcount\">"
                + kHTMLCore.EscapeHtml(CountWords) + "</span><span class=\"sgacts\">"];
            if (nz(Chat.Href).length > 0) {
                const Name: string = kHTMLCore.EscapeAttr("Open the chat with " + Chat.Title);
                Out.push("<a class=\"btn ghost sm sgopen\" href=\"" + kHTMLCore.EscapeAttr(AppSearch.Link(Chat.Href, "", false))
                    + "\" aria-label=\"" + Name + "\" title=\"" + Name + "\">Open chat</a>");
            }
            if (AppSearchActRules.OneToOne(Chat.ChatId) === true) {
                const Who: AppContact = new AppContact();
                Who.ChatId = Chat.ChatId; Who.Title = Chat.Title; Who.Href = Chat.Href;
                Out.push(AppChatQuickView.Button(Who));
            }
            Out.push("</span></div><ul class=\"hits\">");
            for (let I = 0; I < Group.Hits.length; I++) {
                Out.push(AppSearchActView.HitHtml(Group.Hits[I], Group.At[I], Words, Opts));
            }
            Out.push("</ul></li>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActView.GroupHtml");
            return "";
        }
    };

    /** One hit: the open control (`AppSearch.HitHtml`'s link, or the service's context button) and its actions. */
    public static HitHtml = (Hit: AppSearchHit, At: number, Words: string[], Opts: AppSearchActOpts): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Inner: string[] = [];
            const Body: string = AppSearch.SnipHtml(Hit, Words);
            if (Opts.Remote === true) {
                Inner.push("<button type=\"button\" class=\"hitopen\" data-search-chat=\"" + kHTMLCore.EscapeAttr(Hit.Chat.ChatId)
                    + "\" data-search-message=\"" + kHTMLCore.EscapeAttr(Hit.Msg.MessageId) + "\">" + Body + "</button>");
            } else {
                const Older: string = AppSearch.OlderHtml(Hit);
                Inner.push("<a class=\"hitopen\" href=\"" + kHTMLCore.EscapeAttr(AppSearch.Link(Hit.Chat.Href, Hit.Msg.Minute,
                    Hit.Msg.OnPage, Hit.Msg.MessageId)) + "\">" + Body + Older + "</a>");
            }
            Inner.push(AppSearchActView.Acts(Hit, Opts));
            return "<li class=\"hit\" " + AppSearchActRules.HitAttr + "=\"" + String(At) + "\">" + Inner.join("") + "</li>";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActView.HitHtml");
            return "";
        }
    };

    /** The row of icon buttons (+ the six faces, folded until React is pressed) and the hit's status line. */
    public static Acts = (Hit: AppSearchHit, Opts: AppSearchActOpts): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Acts: string[] = AppSearchActRules.ActsFor(Hit, Opts);
            if (Acts.length === 0) { return ""; }
            const Out: string[] = ["<div class=\"sacts\" role=\"group\" aria-label=\"Actions for this message\">"];
            // ⭐ ONE "⋯" ON A PHONE: the row of actions is folded behind it (CSS); pressing it only opens the row.
            Out.push("<button type=\"button\" class=\"btn ghost icon sm samore\" " + AppSearchActRules.ActAttr + "=\""
                + AppSearchActRules.More + "\" aria-expanded=\"false\" aria-label=\"Message actions\" title=\"Message actions\">"
                + AppIcons.Svg(AppIcons.More, 18) + "</button>");
            for (const Act of Acts) {
                const Words: string = kHTMLCore.EscapeAttr(AppSearchActRules.LabelOf(Act));
                let Extra: string = "";
                if (Act === AppSearchActRules.React) { Extra = " aria-expanded=\"false\""; }
                Out.push("<button type=\"button\" class=\"btn ghost icon sm saact\" " + AppSearchActRules.ActAttr + "=\"" + Act
                    + "\" aria-label=\"" + Words + "\" title=\"" + Words + "\"" + Extra + ">"
                    + AppIcons.Svg(AppSearchActRules.IconOf(Act), 18) + "</button>");
            }
            if (Acts.indexOf(AppSearchActRules.React) >= 0) {
                Out.push("<span class=\"safaces\" hidden>");
                for (const Emoji of AppReactRules.Emoji) {
                    const Icon: AppIcon | null = AppIcons.ForEmoji(Emoji);
                    if (Icon === null) { continue; }
                    const Words: string = kHTMLCore.EscapeAttr("React " + (Icon as AppIcon).Label);
                    Out.push("<button type=\"button\" class=\"btn ghost icon sm saface\" " + AppSearchActRules.ActAttr + "=\""
                        + AppSearchActRules.Emoji + "\" " + AppSearchActRules.EmojiAttr + "=\"" + kHTMLCore.EscapeAttr(Emoji)
                        + "\" aria-label=\"" + Words + "\" title=\"" + Words + "\">" + AppIcons.Svg(Icon as AppIcon, 20) + "</button>");
                }
                Out.push("</span>");
            }
            Out.push("<span class=\"sasay\" role=\"status\"></span></div>");
            return Out.join("");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppSearchActView.Acts");
            return "";
        }
    };
}
